/**
 * What Bower accepts as a link href, defined ONCE for both sides of the stack.
 *
 * This was frontend-only, in `components/forms/editor/utils/linkInteraction.ts`,
 * where the editor's `SafeLink` extension uses it to decide whether a stored
 * href renders as an `<a>` or as an inert `<span>`. It moved here when the
 * backend needed the same answer: `parseMarkdownToTipTap` turns untrusted
 * markdown (OCR output, PDF and DOCX extraction, LLM drafts, MCP tool input)
 * into the TipTap JSON we STORE, and markdown link syntax carries whatever URL
 * its author wrote — `[Download](javascript:alert(1))` included.
 *
 * A second copy in the backend was the obvious move and the wrong one: two
 * definitions of "safe URL" drift, and the half that drifts is the half nobody
 * is looking at. One definition, two importers.
 *
 * The policy: absolute `http`/`https` with a real hostname, or `mailto:` with a
 * clean recipient, or an in-app entity path (`inAppEntityPath`), and for
 * STORAGE only an in-document fragment (`#methods`, see `safeStoredLinkHref`).
 * Everything else — `javascript:`, `vbscript:`, `data:`, any other scheme, and
 * every other relative path — is not a link.
 */
import { ARTIFACT_TYPE_CONFIG } from '../constants/artifact-types.generated';
const HTTP_URL = /^https?:\/\//i;
const MAILTO_URL = /^mailto:/i;
const URI_PROTOCOL = /^[a-z][a-z\d+.-]*:/i;
function parseAllowedExplicitHref(value) {
    if (HTTP_URL.test(value)) {
        try {
            const url = new URL(value);
            return url.hostname && (url.protocol === 'http:' || url.protocol === 'https:')
                ? url.href
                : null;
        }
        catch {
            return null;
        }
    }
    if (MAILTO_URL.test(value)) {
        const recipient = value.slice('mailto:'.length).split('?')[0]?.trim();
        if (!recipient || /\s/.test(recipient))
            return null;
        try {
            return new URL(value).href;
        }
        catch {
            return null;
        }
    }
    return null;
}
/** True for an href that may be stored and rendered as a live link, as-is. */
export function isAllowedLinkHref(value) {
    return parseAllowedExplicitHref(value.trim()) !== null;
}
/**
 * Convert a pasted or authored web address into the absolute href we store —
 * `example.com` becomes `https://example.com/`. Returns null when the value
 * cannot be an allowed link, which is the signal to drop the link entirely
 * rather than store something a renderer will refuse anyway.
 */
export function normalizeLinkHref(value) {
    const href = value.trim();
    if (!href)
        return null;
    const explicitHref = parseAllowedExplicitHref(href);
    if (explicitHref)
        return explicitHref;
    if (URI_PROTOCOL.test(href) || href.startsWith('//'))
        return null;
    // A bare DOI (`10.1038/nature12373`) is a citation, not a host: read as one
    // it parses to the IPv4 shorthand `10.0.4.14`, a private address.
    if (BARE_DOI.test(href))
        return new URL(`https://doi.org/${href}`).href;
    try {
        const url = new URL(`https://${href}`);
        if (!url.hostname)
            return null;
        // Any other IPv4 shorthand (`10.1`, `127.1`, `2130706433`) is refused: the
        // URL parser expands it to an address nobody typed.
        if (isDottedQuad(url.hostname) && !href.startsWith(url.hostname))
            return null;
        return url.href;
    }
    catch {
        return null;
    }
}
/** True for a parsed IPv4 host (`a.b.c.d`, digits only). */
function isDottedQuad(hostname) {
    const parts = hostname.split('.');
    return parts.length === 4 && parts.every((part) => /^\d+$/.test(part));
}
/** `10.<registrant>/<suffix>`, the DOI shape (ISO 26324). */
const BARE_DOI = /^10\.\d{4,9}\/\S+$/;
/**
 * First path segments of the app's entity detail pages: every artifact type's
 * `detailRoutePath` (`/notes/:id`, `/protocols/:id`, `/spreadsheets/:id`…) plus
 * `/collections/:id`, which is not an artifact. Derived from the registry so a
 * new artifact type's links survive storage without a second edit here.
 */
const ENTITY_PATH_SEGMENTS = new Set([
    ...Object.values(ARTIFACT_TYPE_CONFIG).flatMap((config) => config.hasDetailPage && config.detailRoutePath ? [config.detailRoutePath.split('/')[1]] : []),
    'collections',
]);
const ENTITY_PATH = /^\/([a-z-]+)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
/**
 * The value itself when it is a link to a Bower entity's own page, exactly
 * `/<entity segment>/<uuid>`, else null.
 *
 * This is the one relative href Bower stores. The agent writes cross-references
 * as `[SDS: HEPES](/notes/<id>)` (its prompt requires the relative form, so a
 * link keeps working on staging, production and a share), and before this the
 * storage policy dropped every one of them from notes while chat rendered them
 * fine: a Bloodless user's SDS column came out as plain text after four turns of
 * the agent reporting success (2026-09-29).
 *
 * Deliberately narrow: no query, fragment, sub-route or non-UUID id, so it
 * names a page and nothing else, and it is same-origin by construction (a
 * single leading slash; `//host` fails the pattern).
 */
export function inAppEntityPath(value) {
    const href = value.trim();
    const match = ENTITY_PATH.exec(href);
    return match && ENTITY_PATH_SEGMENTS.has(match[1]) ? href : null;
}
/** `/docs`, `./x`, `../x`, `#anchor`: a path or a fragment, not a host. A fragment `FRAGMENT` accepts is kept first. */
const RELATIVE_REFERENCE = /^(?:\.{0,2}\/|#)/;
/**
 * `#methods`, `#fn-1`, `#_Toc123`, `#_Ref42`: a fragment-only href, a jump
 * within the same document. Letters, digits and `_ . : % -` only: no `)` or
 * `]`, which would close the markdown link a stored href is written into on
 * export (`#a)![p](https://evil.example/x.png` became an injected image), and
 * no whitespace, quotes or angle brackets. Bounded so an import cannot store
 * an essay as an href.
 */
const FRAGMENT = /^#[\p{L}\p{N}_.:%-]{1,200}$/u;
/**
 * The href to STORE for a link whose target came from outside Bower, or null
 * when there is no safe form and the link mark should be dropped.
 *
 * Almost the expression `SafeLink.renderHTML` evaluates, so stored content
 * matches what the editor renders from it — before this existed the two
 * disagreed, the database holding `javascript:…` that every renderer quietly
 * declined to use.
 *
 * The one deliberate divergence is a relative path (bar `inAppEntityPath`). `normalizeLinkHref` exists
 * for something a person TYPED, where `example.org` obviously means a website,
 * and it reaches that by prefixing `https://` — which turns `/relative/path`
 * into `https://relative/path`, a link to a host that was never mentioned.
 * Harmless to render and wrong to persist: at storage time we are recording
 * what a document said, not guessing what someone meant, and an invented host
 * is a worse record than no link. So a path-like value drops instead.
 *
 * A fragment (`#anchor`) is KEPT, as of 2026-10-23 (David's decision). It
 * points nowhere outside the document, so it invents no host. (The editor does
 * NOT create one from a typed link: `isAllowedLinkUriCandidate('#methods')` is
 * false, so this keeps the anchors that arrive by markdown, import or an agent
 * write.) mammoth emits one per footnote,
 * endnote, cross-reference and table-of-contents entry, and dropping them cost
 * re-export fidelity (`[Introduction](#_Toc123)`); an agent rewriting a block
 * that held one lost it and had to report a "dropped link" for an edit the user
 * asked nothing about (staging, 2026-10-23). A kept fragment still does not
 * NAVIGATE: `normalizeLinkHref('#fn-1')` is null, so `SafeLink` renders it as an
 * inert span (pinned in linkHref.test.ts).
 */
export function safeStoredLinkHref(value) {
    const href = value.trim();
    if (isAllowedLinkHref(href))
        return credentialFree(href);
    const entityPath = inAppEntityPath(href);
    if (entityPath)
        return entityPath;
    if (FRAGMENT.test(href))
        return href;
    if (RELATIVE_REFERENCE.test(href))
        return null;
    const normalized = normalizeLinkHref(href);
    if (normalized === null)
        return null;
    // The fabricated-host guard. `normalizeLinkHref` prefixes `https://` for
    // anything without a scheme, which is right for `example.org` and wrong for
    // `docs/x` — that becomes `https://docs/x`, a host nobody named. A dot is
    // what the editor's own `isAllowedLinkUriCandidate` requires of an autolink
    // candidate, so this is the existing rule rather than a new one.
    //
    // It does not separate `guide.md` from `example.org` — `.md` is a real TLD —
    // and nothing can, from syntax alone. That one is kept, matching what paste
    // does, rather than guessing differently on the storage side.
    try {
        if (!new URL(normalized).hostname.includes('.'))
            return null;
    }
    catch {
        return null;
    }
    return credentialFree(normalized);
}
/**
 * Refuse `https://example.com@evil.com/`. The editor already declines to CREATE
 * one (`isAllowedLinkUriCandidate` rejects `username`/`password`), so without
 * this, storage would accept a shape paste does not — and the phishing value of
 * embedded credentials is entirely in how the host reads to a person.
 */
function credentialFree(href) {
    try {
        const url = new URL(href);
        return url.username || url.password ? null : href;
    }
    catch {
        // Not URL-parseable here means `mailto:`, already validated above.
        return href;
    }
}
/** Longest URL a link artifact stores; matches the `metadata.favicon` cap. */
export const LINK_ARTIFACT_URL_MAX_LENGTH = 4096;
/**
 * The URL a `link` artifact stores as `content.url`, or null when the value
 * cannot be one (docs/planning/link-artifacts.md, D2).
 *
 * `safeStoredLinkHref` plus two narrowings: http(s) only, since a link
 * artifact opens a page in a new tab and `mailto:` is not a page; and the
 * WHATWG-serialized form (`https://Example.com` becomes `https://example.com/`),
 * so the function is idempotent and the stored value can be compared with it.
 */
export function normalizeLinkArtifactUrl(raw) {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.length > LINK_ARTIFACT_URL_MAX_LENGTH)
        return null;
    const safe = safeStoredLinkHref(trimmed);
    if (safe === null)
        return null;
    try {
        const url = new URL(safe);
        if (url.protocol !== 'http:' && url.protocol !== 'https:')
            return null;
        return url.href.length > LINK_ARTIFACT_URL_MAX_LENGTH ? null : url.href;
    }
    catch {
        return null;
    }
}
/**
 * A link's fallback title: the URL's hostname without a leading `www.`, or ''
 * when it does not parse. The web form and the create_artifact tool both title
 * an untitled link with it, so the two never disagree.
 */
export function linkHostname(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    }
    catch {
        return '';
    }
}
//# sourceMappingURL=linkHref.js.map